import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import toast from "react-hot-toast";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

/**
 * CloseChatModal
 *
 * Props:
 *   open            – boolean
 *   onClose         – () => void  (cancel / dismiss)
 *   mainConvId      – string      (main conversation ID)
 *   subConvId       – string|null (currently selected sub-conversation ID, null if on main)
 *   hasSubConvActive – boolean    (whether a sub-conversation is currently active)
 *   onSuccess       – (type: "sub"|"session") => void
 */
const CloseChatModal = ({
  open,
  onClose,
  mainConvId,
  subConvId,
  hasSubConvActive,
  onSuccess,
}) => {
  const [choice, setChoice] = useState("only_this");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (choice === "only_this") {
        // Close only the currently active chat (sub or main)
        const targetId = hasSubConvActive && subConvId ? subConvId : mainConvId;
        await axiosInstance.post(
          ApiEndPoints.CHAT.closeConvertion(targetId),
          { isOpen: false, cascade: false }
        );
        toast.success("chat closed");
        onSuccess?.("sub");
      } else {
        // Close entire session: main conv + all sub-conversations
        await axiosInstance.post(
          ApiEndPoints.CHAT.closeConvertion(mainConvId),
          { isOpen: false, cascade: true }
        );
        toast.success("session closed");
        onSuccess?.("session");
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "failed to close conversation");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setChoice("only_this");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 0.5 },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", pb: 1 }}>
        close conversation
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
          do you want to close this chat only or the entire session?
        </Typography>

        <RadioGroup
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          <FormControlLabel
            value="only_this"
            control={
              <Radio
                size="small"
                sx={{ color: "#6C00E6", "&.Mui-checked": { color: "#6C00E6" } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                only this chat
              </Typography>
            }
          />
          <FormControlLabel
            value="entire_session"
            control={
              <Radio
                size="small"
                sx={{ color: "#6C00E6", "&.Mui-checked": { color: "#6C00E6" } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                entire session
              </Typography>
            }
          />
        </RadioGroup>

        {choice === "entire_session" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mt: 1.5,
              p: 1.5,
              bgcolor: "#FFF7ED",
              borderRadius: 2,
              border: "1px solid #FED7AA",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: "#EA580C", mt: 0.1, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "#9A3412", lineHeight: 1.5 }}>
              this will close all chats in this session.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: "lowercase",
            borderRadius: 2,
            bgcolor: "#6C00E6",
            "&:hover": { bgcolor: "#5800C0" },
            "&.Mui-disabled": { bgcolor: "#E9E3FF", color: "#9CA3AF" },
            minWidth: 90,
          }}
        >
          cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={loading}
          sx={{
            textTransform: "lowercase",
            borderRadius: 2,
            bgcolor: "#6C00E6",
            "&:hover": { bgcolor: "#5800C0" },
            "&.Mui-disabled": { bgcolor: "#E9E3FF", color: "#9CA3AF" },
            minWidth: 90,
          }}
        >
          {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseChatModal;
